import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Filters from "../components/Filters";
import StatCard from "../components/StatCard";
import { EmptyReport, ErrorState } from "../components/EmptyState";
import A5Matrix from "../components/A5Matrix";
import { api, monthLabel, fmtNum, MONTHS } from "../lib/api";
import { loadSignatures } from "../lib/signatures";
import { Users, User, UserCircle2, Home, MapPin, LineChart as LineChartIcon } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

function prevPeriods(tahun, bulan, count = 3) {
  const idx = MONTHS.findIndex((m) => m.value === bulan);
  const out = [];
  let year = parseInt(tahun, 10);
  let month = idx;
  for (let i = 0; i < count; i++) {
    if (month < 0) { month = 11; year -= 1; }
    out.unshift({ tahun: String(year), bulan: MONTHS[month].value });
    month -= 1;
  }
  return out;
}

export default function DetailRt() {
  const [sp] = useSearchParams();
  const [filter, setFilter] = useState({
    tahun: sp.get("tahun") || "2026",
    bulan: sp.get("bulan") || "september",
    rw: sp.get("rw") || "01",
    rt: sp.get("rt") || "001",
  });
  const [a5, setA5] = useState(null);
  const [sig, setSig] = useState(null);
  const [tren, setTren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!filter.rw || !filter.rt) return;
    setLoading(true); setError(false);
    try {
      const periode = `${monthLabel(filter.bulan)} ${filter.tahun}`;
      const periods = prevPeriods(filter.tahun, filter.bulan, 3);
      const [current, sigRes, ...trenRes] = await Promise.all([
        api.a5(filter.tahun, filter.bulan, filter.rw, filter.rt),
        loadSignatures("rt", filter.rw, filter.rt, filter.bulan, filter.tahun),
        ...periods.map((p) => api.a5(p.tahun, p.bulan, filter.rw, filter.rt).catch(() => ({ data: null }))),
      ]);
      setA5(current);
      setSig(sigRes);
      setTren(periods.map((p, i) => {
        const rr = trenRes[i]?.data?.ringkasan || {};
        return {
          label: `${monthLabel(p.bulan).slice(0, 3)} '${p.tahun.slice(2)}`,
          laki: rr.laki_laki || 0,
          perempuan: rr.perempuan || 0,
          total: rr.total_penduduk || 0,
        };
      }));
    }
    catch { setError(true); } finally { setLoading(false); }
  }, [filter.tahun, filter.bulan, filter.rw, filter.rt]);
  useEffect(() => { load(); }, [load]);

  const d = a5?.data;
  const r = d?.ringkasan || {};
  const k = d?.kelompok || {};

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0e2b26] tracking-tight">Detail RT</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          {filter.rw && filter.rt ? <>RW <strong>{filter.rw}</strong> / RT <strong>{filter.rt}</strong> · </> : null}
          Periode <strong>{monthLabel(filter.bulan)} {filter.tahun}</strong>
        </p>
      </div>
      <Filters value={filter} onChange={setFilter} onRefresh={load} showRw showRt />

      {(() => {
        if (error) return <ErrorState onRetry={load} />;
        if (!filter.rw || !filter.rt) {
          return (
            <div className="bg-white border border-dashed rounded-xl p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">
              Pilih RW dan RT untuk melihat detail.
            </div>
          );
        }
        if (loading) {
          return (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={`sk-detail-${i}`} className="stat-card"><div className="skeleton h-3 w-24 mb-2"></div><div className="skeleton h-8 w-16"></div></div>
              ))}
            </div>
          );
        }
        if (!d) return <EmptyReport />;
        return (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            <StatCard label="Total Penduduk" value={r.total_penduduk || 0} sub="Jiwa" icon={Users} tone="default" />
            <StatCard label="Laki-laki" value={r.laki_laki || 0} sub="Jiwa" icon={User} tone="blue" />
            <StatCard label="Perempuan" value={r.perempuan || 0} sub="Jiwa" icon={UserCircle2} tone="pink" />
            <StatCard label="Penduduk Tetap" value={r.penduduk_tetap || 0} sub="Jiwa" icon={Home} tone="amber" />
            <StatCard label="Penduduk Sementara" value={r.penduduk_sementara || 0} sub="Jiwa" icon={MapPin} tone="slate" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="chart-card">
              <h3 className="font-bold text-[#0e2b26] mb-3">Mutasi Bulan Ini</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {["lahir","meninggal","pindah","datang"].map((key) => {
                  const t = k.tetap?.[key] || { l: 0, p: 0 };
                  const b = k.bandung?.[key] || { l: 0, p: 0 };
                  const j = k.jabar?.[key] || { l: 0, p: 0 };
                  const lj = k.luar_jabar?.[key] || { l: 0, p: 0 };
                  const totalL = (t.l||0)+(b.l||0)+(j.l||0)+(lj.l||0);
                  const totalP = (t.p||0)+(b.p||0)+(j.p||0)+(lj.p||0);
                  const mutasiLabels = { lahir: "Kelahiran", meninggal: "Kematian", pindah: "Pindah", datang: "Datang" };
                  return (
                    <div key={key} className="rounded-lg border border-[hsl(var(--border))] p-3">
                      <div className="text-xs font-bold uppercase text-[hsl(var(--muted-foreground))] tracking-wider">{mutasiLabels[key]}</div>
                      <div className="text-xl font-extrabold text-[#0e2b26] mt-1">{fmtNum(totalL + totalP)}</div>
                      <div className="text-xs text-[hsl(var(--muted-foreground))]">L: {totalL} · P: {totalP}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="chart-card">
              <h3 className="font-bold text-[#0e2b26] mb-3">Informasi Laporan</h3>
              <dl className="text-sm space-y-2">
                <div className="flex justify-between border-b pb-1.5"><dt className="text-[hsl(var(--muted-foreground))]">ID Laporan</dt><dd className="font-semibold">{d.id_laporan || "-"}</dd></div>
                <div className="flex justify-between border-b pb-1.5"><dt className="text-[hsl(var(--muted-foreground))]">Periode</dt><dd className="font-semibold">{d.periode || "-"}</dd></div>
                <div className="flex justify-between border-b pb-1.5"><dt className="text-[hsl(var(--muted-foreground))]">Tanggal Submit</dt><dd className="font-semibold">{d.timestamp || "-"}</dd></div>
                <div className="flex justify-between border-b pb-1.5"><dt className="text-[hsl(var(--muted-foreground))]">Kelurahan</dt><dd className="font-semibold">{d.kelurahan || "-"}</dd></div>
                <div className="flex justify-between"><dt className="text-[hsl(var(--muted-foreground))]">Catatan</dt><dd className="font-semibold">{d.catatan || "-"}</dd></div>
              </dl>
            </div>
          </div>

          <div className="chart-card">
            <div className="flex items-center gap-2 mb-3">
              <LineChartIcon size={16} className="text-[#0e6b4f]" />
              <h3 className="font-bold text-[#0e2b26]">Tren 3 Bulan Terakhir · RW {filter.rw} / RT {filter.rt}</h3>
            </div>
            {tren.every((t) => t.total === 0) ? (
              <div className="text-sm text-[hsl(var(--muted-foreground))] py-8 text-center">
                Belum ada data historis untuk RT ini pada 3 bulan terakhir.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={tren} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="total" name="Total" stroke="#0f766e" strokeWidth={2.5} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="laki" name="Laki-laki" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="perempuan" name="Perempuan" stroke="#f472b6" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <A5Matrix
            data={d}
            periode={`${monthLabel(filter.bulan)} ${filter.tahun}`}
            rw={filter.rw}
            rt={filter.rt}
            scope="rt"
            signature={sig}
            bulan={filter.bulan}
            tahun={filter.tahun}
          />
        </>
        );
      })()}
    </div>
  );
}
